import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Quote from '../components/Quote';
import { SiteContent } from '../content';

const SearchIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" /></svg>
);

const Shop: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState('All');
  const [search, setSearch] = useState('');

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.01, rootMargin: "0px 0px 100px 0px" });

    const elements = containerRef.current?.querySelectorAll('.animate-on-scroll');
    elements?.forEach(el => {
      if (el.getBoundingClientRect().top >= window.innerHeight) {
        el.classList.add('below-fold');
        observer.observe(el);
      }
    });

    return () => observer.disconnect();
  }, [filter, search]);

  const { hero, framework, explanation, products, collection, assets, closing } = SiteContent.shop;

  const filteredAssets = assets.filter(item => {
    const matchesSearch = item.name.toLowerCase().includes(search.toLowerCase());
    const matchesFilter = filter === 'All' || item.type === filter;
    return matchesSearch && matchesFilter;
  });

  const categories = [
    { label: 'ALL', value: 'All' },
    { label: 'LETTERS', value: 'Letter' },
    { label: 'NUMBERS', value: 'Number' },
  ];


  return (
    <div ref={containerRef} className="flex flex-col w-full bg-paper min-h-screen overflow-hidden">

      {/* SECTION 1: HERO */}
      <section className="pt-32 md:pt-48 pb-16 text-center px-6">
        <div className="max-w-7xl mx-auto">
          <span className="editorial-label">CURATED COLLECTION</span>
          <div className="animate-on-scroll animate-fade-up">
            <h1 className="hero-title !text-rich-black mb-8 leading-[0.9]">{hero.title}</h1>
            <p className="text-xl md:text-2xl text-[#1A1A1A]/60 font-medium italic mb-8">{hero.description}</p>
            <Quote text={hero.quote} className="mb-6" />
            <p className="serif italic text-lg text-[#A65D3F]/80 max-w-2xl mx-auto">{hero.sacredGeometry}</p>
          </div>
        </div>
      </section>

      {/* SECTION 2: THE COLLECTION — Art First */}
      <section className="bg-[#F2E9D9]/30 pt-24">
        <div className="text-center mb-12 animate-on-scroll animate-fade-up">
          <h2 className="section-title mb-4">{collection.title}</h2>
          <p className="text-lg italic serif text-black/60">{collection.subtitle}</p>
        </div>

        {/* FILTER BAR */}
        <div className="sticky top-[70px] md:top-[85px] z-50 bg-[#F2E9D9]/95 backdrop-blur-md border-y border-black/5 py-4 px-6">
          <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center space-x-6 overflow-x-auto w-full md:w-auto no-scrollbar py-1">
              {categories.map(cat => (
                <button
                  key={cat.value}
                  onClick={() => setFilter(cat.value)}
                  className={`text-xs font-black uppercase tracking-[0.4em] transition-all whitespace-nowrap border-b-2 py-3 px-2 ${filter === cat.value ? 'text-[#A65D3F] border-[#A65D3F]' : 'text-black/40 border-transparent hover:text-black'}`}
                >
                  {cat.label}
                </button>
              ))}
            </div>
            <div className="relative w-full md:w-72">
              <input
                type="text"
                placeholder="SEARCH LETTERS, NUMBERS..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full bg-black/5 border-none rounded-sm py-3 pl-10 pr-4 text-xs font-bold uppercase tracking-widest focus:ring-1 focus:ring-[#A65D3F] outline-none"
              />
              <div className="absolute left-3.5 top-1/2 -translate-y-1/2 text-black/20">
                <SearchIcon />
              </div>
            </div>
          </div>
        </div>

        {/* PRODUCT GRID — Letters & Numbers */}
        <div className="py-16 px-4 md:px-12 bg-white/10">
          <div className="max-w-7xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12">
            {filteredAssets.map((item, i) => (
                <div key={item.name} className="animate-on-scroll animate-scale-in group">
                  {/* Image — links to NFT gallery */}
                  <a href={item.marketplaceUrl} target="_blank" rel="noopener noreferrer">
                    <div className="relative aspect-square overflow-hidden rounded-sm img-placeholder shadow-sm group-hover:shadow-xl transition-shadow duration-500">
                      <img
                        src={item.img}
                        alt={item.name}
                        loading="lazy"
                        className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110"
                        onLoad={(e) => (e.currentTarget.parentElement?.classList.remove('img-placeholder'))}
                      />
                    </div>
                  </a>

                  {/* Minimal link bar */}
                  <div className="flex items-center justify-center gap-0 pt-4">
                    <a
                      href={item.marketplaceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-black uppercase tracking-widest text-black/50 hover:text-[#A65D3F] transition-colors py-2 px-3"
                    >
                      MINT 0.23 SOL
                    </a>
                    <span className="text-black/20">|</span>
                    <a
                      href="https://shop.earthrightsbusiness.com"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs font-black uppercase tracking-widest text-black/50 hover:text-[#A65D3F] transition-colors py-2 px-3"
                    >
                      BUY $100
                    </a>
                  </div>
                </div>
            ))}
          </div>

          {filteredAssets.length === 0 && (
            <div className="py-40 text-center">
              <p className="serif italic text-2xl text-black/20">No matching frequencies found in the soil.</p>
              <button onClick={() => { setFilter('All'); setSearch(''); }} className="mt-8 text-xs font-black uppercase tracking-widest border-b border-black/20 pb-1 py-3 px-4 hover:text-[#A65D3F] hover:border-[#A65D3F] transition-colors">Show All Assets</button>
            </div>
          )}
        </div>

      </section>

      {/* SECTION 3: PARTICIPATE / COMMUNICATE / REGENERATE */}
      <section className="py-24 bg-paper px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8 md:gap-16">
            {framework.steps.map((step: { title: string; description: string }, i: number) => (
              <div key={i} className="text-center animate-on-scroll animate-fade-up" style={{ transitionDelay: `${i * 150}ms` }}>
                <div className="text-5xl font-black text-[#A65D3F] mb-6 opacity-30">0{i + 1}</div>
                <h3 className="font-condensed text-xl font-black tracking-widest uppercase mb-4">{step.title}</h3>
                <p className="text-sm text-black/70 leading-relaxed font-medium">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4: EXPLANATION - Editorial Layout */}
      <section className="pb-24 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="animate-on-scroll animate-mask rounded-xl overflow-hidden shadow-2xl aspect-[16/9] img-placeholder">
              <img
                src="/images/Hand Holding Letter.png"
                alt="Hand holding an ERB letter art piece"
                className="w-full h-full object-cover opacity-90"
                loading="lazy"
              />
            </div>
          </div>
          <div className="lg:col-span-5 lg:pl-12">
            <span className="editorial-label">THE UTILITY</span>
            <h2 className="section-title text-3xl md:text-5xl mb-8">{explanation.title}</h2>
            <p className="text-lg md:text-xl font-medium text-black/70 mb-10 leading-relaxed">
              {explanation.text}
            </p>
            <div className="space-y-4">
              {explanation.receive.map((item, i) => (
                <div key={i} className="animate-on-scroll animate-fade-up" style={{ transitionDelay: `${i * 100}ms` }}>
                  <span className="font-bold block text-sm mb-1">{item.label}</span>
                  <span className="text-xs text-black/60 uppercase tracking-widest leading-none">{item.desc}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SECTION 5: PRODUCTS */}
      <section className="pb-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-on-scroll animate-fade-up">
            <span className="editorial-label">{products.title}</span>
          </div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-16">
            {products.items.map((prod, i) => (
              <div key={i} className="bg-white border border-black/5 p-8 md:p-12 text-center animate-on-scroll animate-scale-in" style={{ transitionDelay: `${i * 100}ms` }}>
                <h3 className="font-black text-xl mb-4 text-[#A65D3F] uppercase tracking-widest">{prod.type}</h3>
                <div className="text-4xl md:text-5xl font-black mb-8 text-rich-black">{prod.price}</div>

                <ul className="space-y-4 mb-10 text-left max-w-xs mx-auto">
                  {prod.features.map((feat, j) => (
                    <li key={j} className="flex items-center gap-3 text-xs font-bold tracking-widest uppercase opacity-60">
                      <span className="w-1.5 h-1.5 bg-[#A65D3F] rounded-full"></span>
                      {feat}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6: CLOSING */}
      {(closing.cta === "CONTACT US" &&
        <section className="py-32 md:py-48 bg-rich-black text-center relative overflow-hidden">
          <img
            src="/images/home-hero.jpg"
            alt=""
            className="absolute inset-0 w-full h-full object-cover opacity-10 -scale-x-100"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/30 to-transparent" />
          <div className="vertical-text right-12 top-1/2 -translate-y-1/2 !text-white/5">
            {closing.title && closing.title.toUpperCase()}
          </div>
          <div className="max-w-4xl mx-auto px-6 relative z-10">
            <h3 className="section-title text-white mb-8">{closing.title}</h3>
            <p className="text-white/70 text-lg md:text-xl font-medium mb-12 max-w-2xl mx-auto">{closing.text}</p>
            <Quote large text={closing.quote} className="mb-12" />
            <div className="animate-on-scroll animate-scale-in delay-200">
              <Link to="/contact" className="btn-primary !px-12">{closing.cta}</Link>
            </div>
          </div>
        </section>
      )}
    </div>

  );
};

export default Shop;
